import React from 'react'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { GroupTicketsModalTypes } from './GroupTicketsModal'
import { ShopifyGroupTickets } from './ShopifyGroupTickets'
import { ShopifyGroupTicketsDiscountVariation } from './ShopifyGroupTicketsDiscountVariation'

interface Props {
  setModalType: React.Dispatch<React.SetStateAction<GroupTicketsModalTypes>>
  ticketPrice: number
  theatricalSlug: string
  groupTicketProductVariantId: string
}

export const GroupTicketsDiscountExperiment: React.FC<Props> = ({
  setModalType,
  ticketPrice,
  theatricalSlug,
  groupTicketProductVariantId,
}) => {
  const { query } = useSafeRouter()
  const groupPromo = getItemOrFirstEntry(query?.groupDiscountCode)

  if (groupPromo) {
    return (
      <ShopifyGroupTickets
        setModalType={setModalType}
        ticketPrice={ticketPrice}
        theatricalSlug={theatricalSlug}
        groupTicketProductVariantId={groupTicketProductVariantId}
      />
    )
  }

  return (
    <GBFadeInExperiment defaultValue="control" experimentName="group-ticket-discounts-percentages">
      {/* Control - Current buy 5 get 5 free model */}
      <ExperimentVariation variation="control">
        <ShopifyGroupTickets
          setModalType={setModalType}
          ticketPrice={ticketPrice}
          theatricalSlug={theatricalSlug}
          groupTicketProductVariantId={groupTicketProductVariantId}
        />
      </ExperimentVariation>

      {/* 40% OFF for 10/20 tickets, 50% OFF for 50/100/200 tickets */}
      <ExperimentVariation variation="40OFF">
        <ShopifyGroupTicketsDiscountVariation
          setModalType={setModalType}
          ticketPrice={ticketPrice}
          theatricalSlug={theatricalSlug}
          variantNumber={1}
          groupTicketProductVariantId={groupTicketProductVariantId}
          discountRates={[
            { quantity: 10, discountRate: 0.6 },
            { quantity: 20, discountRate: 0.6 },
            { quantity: 50, discountRate: 0.5 },
            { quantity: 100, discountRate: 0.5 },
            { quantity: 200, discountRate: 0.5 },
          ]}
          discountLabels={[
            { quantity: 10, label: '40% OFF' },
            { quantity: 20, label: '40% OFF' },
            { quantity: 50, label: '50% OFF' },
            { quantity: 100, label: '50% OFF' },
            { quantity: 200, label: '50% OFF' },
          ]}
        />
      </ExperimentVariation>

      {/* 30% OFF for 10/20 tickets, 40% OFF for 50/100/200 tickets */}
      <ExperimentVariation variation="30OFF">
        <ShopifyGroupTicketsDiscountVariation
          setModalType={setModalType}
          ticketPrice={ticketPrice}
          theatricalSlug={theatricalSlug}
          variantNumber={2}
          groupTicketProductVariantId={groupTicketProductVariantId}
          discountRates={[
            { quantity: 10, discountRate: 0.7 },
            { quantity: 20, discountRate: 0.7 },
            { quantity: 50, discountRate: 0.6 },
            { quantity: 100, discountRate: 0.6 },
            { quantity: 200, discountRate: 0.6 },
          ]}
          discountLabels={[
            { quantity: 10, label: '30% OFF' },
            { quantity: 20, label: '30% OFF' },
            { quantity: 50, label: '40% OFF' },
            { quantity: 100, label: '40% OFF' },
            { quantity: 200, label: '40% OFF' },
          ]}
        />
      </ExperimentVariation>

      {/* 20% OFF for 10/20 tickets, 30% OFF for 50/100/200 tickets */}
      <ExperimentVariation variation="20OFF">
        <ShopifyGroupTicketsDiscountVariation
          setModalType={setModalType}
          ticketPrice={ticketPrice}
          theatricalSlug={theatricalSlug}
          variantNumber={3}
          groupTicketProductVariantId={groupTicketProductVariantId}
          discountRates={[
            { quantity: 10, discountRate: 0.8 },
            { quantity: 20, discountRate: 0.8 },
            { quantity: 50, discountRate: 0.7 },
            { quantity: 100, discountRate: 0.7 },
            { quantity: 200, discountRate: 0.7 },
          ]}
          discountLabels={[
            { quantity: 10, label: '20% OFF' },
            { quantity: 20, label: '20% OFF' },
            { quantity: 50, label: '30% OFF' },
            { quantity: 100, label: '30% OFF' },
            { quantity: 200, label: '30% OFF' },
          ]}
        />
      </ExperimentVariation>

      {/* Default - Fallback to control */}
      <ExperimentVariation default>
        <ShopifyGroupTickets
          setModalType={setModalType}
          ticketPrice={ticketPrice}
          theatricalSlug={theatricalSlug}
          groupTicketProductVariantId={groupTicketProductVariantId}
        />
      </ExperimentVariation>
    </GBFadeInExperiment>
  )
}
